import React, { useEffect, useMemo, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { cx } from '@/utils';
import Cookies from 'js-cookie';

import { ROUTE_ADMIN, ROUTE_ADMIN_HOME } from '@/utils/constants';
import { usePagePermission } from '@/utils/hooks/usePagePermission';
import { showErrorToast } from '@/utils/toast/toastHelpers';

import LockedIcon from '../../Icons/LockedIcon';
import ModalCustom from '../../Modals/ModalCustom';
import ModalDialog from '../../Modals/ModalDialog';
import ModalGeneral from '../../Modals/ModalGeneral';
import ModalLoading from '../../Modals/ModalLoading';
import { SideMenuAdmin } from '../../SideMenu/SideMenuAdmin';
import NewAdminFooter from '../Admin/Footer/NewAdminFooter';
import NewAdminHeader from '../Admin/Header/NewAdminHeader';
import LayoutTheme from '../LayoutTheme/LayoutTheme';

interface IHeaderProps {
  userToken: string | undefined;
  dependencyId: string | undefined;
}
const Header = React.memo(({ userToken, dependencyId }: IHeaderProps) => (
  <header className="z-sticky sticky top-0">
    <NewAdminHeader hideRightSection={userToken && dependencyId ? false : true} />
  </header>
));

Header.displayName = 'Header';

interface AdminLayoutProps {
  children: ReactNode;
  showSideMenu: boolean;
}

const AdminLayout: React.FC<AdminLayoutProps> = React.memo(({ children, showSideMenu }) => {
  const router = useRouter();
  const userToken = Cookies.get('token_admin');
  const dependencyId = Cookies.get('dependencyId');
  const isAdminRoute = router.pathname.startsWith(ROUTE_ADMIN);
  const isLoginRoute = router.pathname === ROUTE_ADMIN;
  const { userHasPermission } = usePagePermission();
  useEffect(() => {
    if ((!userToken || !dependencyId) && isAdminRoute && !isLoginRoute) {
      router.push(ROUTE_ADMIN);
    } else if (userToken && isLoginRoute && dependencyId) {
      window.location.href = ROUTE_ADMIN_HOME;
    }
  }, [userToken, isAdminRoute, isLoginRoute, router]);

  useEffect(() => {
    if (!userToken && router.query.p) {
      showErrorToast('Debe iniciar sesión para acceder a esta página', { autoClose: false });
    }
  }, [userToken, router.query.p]);

  const sideMenu = useMemo(
    () => (showSideMenu && userToken ? <SideMenuAdmin /> : null),
    [showSideMenu, userToken]
  );

  const content = (
    <div className="data-admin-true grid min-h-screen grid-rows-[55px_1fr_55px]" data-admin="true">
      {/* Header */}
      <div className="row-start-1 row-end-2 h-[55px]">
        <Header userToken={userToken} dependencyId={dependencyId} />
      </div>
      {/* Contenido principal con sideMenu y main */}
      <div
        className={cx('row-start-2 row-end-3 grid min-h-0 min-w-0 ', {
          'pt-[30px]': !showSideMenu,
          'pt-[0px]': !userToken || !dependencyId,
          'grid-cols-[auto_1fr]': showSideMenu,
        })}
      >
        {sideMenu}
        {userHasPermission ? (
          <main
            className={cx('min-h-0 w-full min-w-0 flex-1 overflow-auto', { 'pt-3': showSideMenu })}
          >
            {children}
          </main>
        ) : (
          <main className={cx('min-h-0 min-w-0 flex-1 overflow-auto', { 'pt-3': showSideMenu })}>
            <div className="flex h-full flex-col items-center justify-center">
              <LockedIcon className="mb-4 h-auto w-[320px] text-gray-500" />
              <h1 className="text-[32px] font-bold text-primary">Acceso denegado</h1>
              <p className="text-text-gray-400 text-xl">
                No tienes permiso para acceder a esta página.
              </p>
            </div>
          </main>
        )}
      </div>

      {/* Footer */}
      <div className="row-start-3 row-end-4 h-[55px]">
        <NewAdminFooter isLoginFooter={!userToken} />
      </div>

      <ModalGeneral />
      <ModalCustom />
      <ModalDialog />
      <ModalLoading />
    </div>
  );

  return <LayoutTheme>{content}</LayoutTheme>;
});

AdminLayout.displayName = 'AdminLayout';

export default AdminLayout;
